import { Home, Flag, Users, BarChart3, LogOut } from "lucide-react";
import { useIsDesktop } from "../hooks/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { usePageConfig } from "../hooks/usePageConfig";
import DesignSwitcher from "../redesign/DesignSwitcher";
import LineSwitcher from "./LineSwitcher";

const sidebarItems = [
  { id: "home", icon: Home, label: "HOME", disabled: true },
  { id: "cases", icon: Flag, label: "CASES", path: "/cases" },
  { id: "people", icon: Users, label: "USERS", path: "/people" },
  { id: "analytics", icon: BarChart3, label: "ANALYTICS", path: "/analytics" },
  { id: "logout", icon: LogOut, label: "LOGOUT", path: "/logout" },
];

export default function Sidebar({ open = false, onNavigate }) {
  const isDesktop = useIsDesktop();
  const { pageConfig } = usePageConfig();
  const showClientLogo = pageConfig.branding?.showClientLogo ?? false;

  const location = useLocation();
  const navigate = useNavigate();

  // Property is a parallel vertical with its own list and analytics pages, so
  // while the user is inside it the nav points at those rather than motor's.
  // Everything else (Users, Home, Logout) is shared between the two.
  const onProperty = location.pathname.startsWith("/property-");
  const resolvePath = (item) => {
    if (!onProperty) return item.path;
    if (item.id === "cases") return "/property-cases";
    if (item.id === "analytics") return "/property-analytics";
    return item.path;
  };

  const handleLogout = () => {
    // 🔹 Clear session/token if needed
    // localStorage.removeItem("token");

    navigate("/");
  };

  return (
    <div
      /* A transform still leaves the rail in the tab order and the
         accessibility tree, so with the drawer closed Tab walked into
         links nobody could see. inert removes both. Never at lg. */
      {...(!open && !isDesktop ? { inert: true } : {})}
      className={[
        "flex w-24 flex-col justify-between border-r border-border-dark bg-background-dark px-6 py-6",
        // Same drawer treatment as the redesign rail, driven by the same
        // state in Layout.js. 96px is a quarter of a 375px screen.
        "fixed inset-y-0 left-0 z-fixed shadow-xl transition-transform",
        // The rail is pinned to the viewport height here, so a long nav
        // (Hegic folds in Break-in and PAYD) needs somewhere to go.
        "overflow-y-auto",
        open ? "translate-x-0" : "-translate-x-full",
        "lg:static lg:h-full lg:translate-x-0 lg:shadow-none",
      ].join(" ")}>
      {/* Top Section */}
      <div className="flex flex-col items-center gap-11">
        {/* Logo */}
        {showClientLogo && (
          <div className="flex h-16 items-center">
            <div className="flex flex-col">
              <img src="/assets/logos/ergo-logo.png" alt="Logo" />
            </div>
          </div>
        )}

        {/* Navigation Items */}
        <div className="flex flex-col items-center gap-0">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            // Special logic for cases to be active on both /cases and /cases/:id
            const path = resolvePath(item);
            const isActive =
              item.id === "cases"
                ? location.pathname.startsWith(onProperty ? "/property-cases" : "/cases")
                : path
                ? location.pathname.startsWith(path)
                : false;
            const isDisabled = Boolean(item.disabled);
            const handleClick = (e) => {
              if (isDisabled) {
                e.preventDefault();
                return;
              }

              if (item.path === "/logout") {
                e.preventDefault();
                handleLogout();
              }
            };

            const content = (
              <>
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl">
                  <Icon
                    className={`h-8 w-8 ${
                      isActive && !isDisabled
                        ? "text-white"
                        : !isDisabled 
                          ? "text-text-secondary" 
                          : "text-gray-600"
                    }`}
                  />
                </div>
                <span
                  className={`text-center text-xs font-bold uppercase leading-none tracking-tight ${
                    isActive && !isDisabled
                      ? "text-white underline underline-offset-[6px] decoration-2"
                      : !isDisabled
                        ? "text-text-secondary"
                        : "text-gray-600"
                  }`}
                >
                  {item.label}
                </span>
              </>
            );

            return (
              isDisabled ? (
                <div
                  key={item.id}
                  aria-disabled="true"
                  className="flex w-full cursor-not-allowed flex-col items-center gap-1.5 rounded-2xl py-2 opacity-60"
                >
                  {content}
                </div>
              ) : (
                <Link
                  key={item.id}
                  to={item.path === "/logout" ? "#" : path}
                  onClick={handleClick}
                  data-tour={`nav-${item.id}`}
                  className="flex w-full flex-col items-center gap-1.5 rounded-2xl py-2"
                >
                  {content}
                </Link>
              )
            );
          })}

          {/* Motor or property, for an Ergo admin. */}
          <LineSwitcher variant="icon" />

          {/* The A/B toggle, in the rail rather than floating over the page. */}
          <DesignSwitcher variant="icon" />
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-8 w-8 items-center justify-center">
          <img
            src="https://api.builder.io/api/v1/image/assets/TEMP/60d80dc90582a1d0492b0dd3ef3cde4577af1816?width=64"
            alt="Footer Logo"
            className="h-8 w-8"
          />
        </div>
        <div className="w-16 text-center text-xs font-bold uppercase leading-none text-white">
          POWERED BY CAMCOM
        </div>
      </div>
    </div>
  );
}
